import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown, Copy, MoreVertical, TextSearch, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { TextIndex } from '@rushdb/javascript-sdk'
import type { Project } from '~/features/projects/types'

import { Button } from '~/elements/Button'
import { Card } from '~/elements/Card'
import { ConfirmDialog } from '~/elements/ConfirmDialog'
import { Dialog, DialogTitle } from '~/elements/Dialog'
import { IconButton } from '~/elements/IconButton'
import { TextField } from '~/elements/Input'
import { Label } from '~/elements/Label'
import { Menu, MenuItem } from '~/elements/Menu'
import { NothingFound } from '~/elements/NothingFound'
import { SearchSelect, SelectItem } from '~/elements/SearchSelect'
import { Skeleton } from '~/elements/Skeleton'
import { getLabelColor } from '~/features/labels'
import { LabelName } from '~/features/labels/components/LabelName'
import { useProjectLabelsQuery } from '~/features/projects/hooks/useProjectQueries'
import { api } from '~/lib/api'
import { cn, copyToClipboard } from '~/lib/utils'

import { useAddTextIndexMutation, useDeleteTextIndexMutation } from '../hooks/useIndexMutations'
import { indexNameProblem, useLabelPropertiesQuery } from './AddIndex'

/** The property of an index over every string of the record. */
const ALL_STRINGS = '*'

const propertyText = (property: string) => (property === ALL_STRINGS ? 'All text' : property)

const STATUS_COLORS: Record<TextIndex['status'], string> = {
  building: 'text-badge-blue border-badge-blue/30 bg-badge-blue/10',
  ready: 'text-badge-green border-badge-green/30 bg-badge-green/10',
  dropping: 'text-content-2 border-border bg-background'
}

function TextIndexListItem({ index, loading }: { index?: TextIndex; loading?: boolean }) {
  const { mutate } = useDeleteTextIndexMutation()
  const { data: labels } = useProjectLabelsQuery()
  const { name, label, property, consistency, status, pending } = index ?? {}
  const labelVariant = label ? getLabelColor(label, Object.keys(labels ?? {}).indexOf(label)) : undefined

  return (
    <li className="flex items-center gap-3 px-3 py-3 sm:gap-4 sm:px-4">
      <TextSearch size={20} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="mb-1 truncate font-mono text-sm font-medium text-content" title={name}>
          <Skeleton enabled={loading}>{name ?? 'Loading...'}</Skeleton>
        </span>
        <span className="flex items-center gap-1.5">
          <Skeleton enabled={loading}>
            <Label variant={labelVariant}>{label}</Label>
            <span className="text-content3">:</span>
            <span className="w-fit rounded-sm bg-content3/10 px-1 font-mono text-xs text-content2">
              {propertyText(property ?? '')}
            </span>
          </Skeleton>
        </span>
      </div>
      {!loading && pending ?
        <span className="text-sm text-content3 tabular-nums" title="Changes not applied yet">
          {pending.toLocaleString()} pending
        </span>
      : null}
      {!loading && (
        <span className="text-sm text-content3" title="When searches see a change">
          {consistency === 'immediate' ? 'Immediate' : 'Eventual'}
        </span>
      )}
      {status && (
        <span
          className={cn(
            'inline-flex items-center rounded-md border px-1.5 py-0.5 font-medium capitalize',
            STATUS_COLORS[status]
          )}
        >
          {status}
        </span>
      )}
      {!loading && name && (
        <Menu
          align="end"
          trigger={
            <IconButton aria-label="more" title="More" variant="ghost">
              <MoreVertical />
            </IconButton>
          }
        >
          <MenuItem icon={<Copy />} onClick={() => copyToClipboard(name, { showSuccessToast: true })}>
            Copy index name
          </MenuItem>
          <ConfirmDialog
            description={`The text index "${name}" will be dropped. Are you sure?`}
            handler={() => {
              mutate({ name })
              return Promise.resolve()
            }}
            title="Drop text index"
            trigger={
              <MenuItem dropdown icon={<Trash2 />} variant="danger">
                Drop
              </MenuItem>
            }
          />
        </Menu>
      )}
    </li>
  )
}

export function TextIndexesList({ data, loading }: { data?: TextIndex[]; loading: boolean }) {
  if (data && data.length < 1) {
    return <NothingFound title="No text indexes yet" />
  }
  return (
    <Card>
      <ul className="flex flex-col divide-y">
        {data?.map((index) => (
          <TextIndexListItem index={index} key={index.name} />
        ))}
        {loading ?
          <>
            <TextIndexListItem loading />
            <TextIndexListItem loading />
          </>
        : null}
      </ul>
    </Card>
  )
}

export function AddTextIndexDialog({
  existingIndexes,
  projectId,
  trigger
}: {
  existingIndexes?: TextIndex[]
  projectId: Project['id']
  trigger: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog className="sm:max-w-2xl" onOpenChange={setOpen} open={open} trigger={trigger}>
      <DialogTitle>Create text index</DialogTitle>
      <p className="mt-2 text-content2">
        Choose a label and a text property, or all of its text, to make it searchable by keywords.
      </p>
      {open && (
        <AddTextIndexForm
          existingIndexes={existingIndexes}
          onCreated={() => setOpen(false)}
          projectId={projectId}
        />
      )}
    </Dialog>
  )
}

function AddTextIndexForm({
  existingIndexes,
  onCreated,
  projectId
}: {
  existingIndexes?: TextIndex[]
  onCreated: () => void
  projectId: Project['id']
}) {
  const { mutateAsync: create, isPending } = useAddTextIndexMutation()
  const { data: labels, isPending: loadingLabels } = useProjectLabelsQuery()
  const [label, setLabel] = useState('')
  const [property, setProperty] = useState('')
  const [step, setStep] = useState<'select' | 'name'>('select')
  const [name, setName] = useState('')
  const { data: properties = [], isFetching: loadingProperties } = useLabelPropertiesQuery(label)
  const labelEntries = useMemo(() => Object.entries(labels ?? {}), [labels])
  const labelIdx = Math.max(
    labelEntries.findIndex(([labelName]) => labelName === label),
    0
  )
  const nameSuggestion = useQuery({
    queryKey: ['text-index-name-suggestion', projectId, label, property],
    queryFn: () => api.textIndexes.suggestName({ projectId, label, property }),
    enabled: step === 'name',
    staleTime: 0
  })
  useEffect(() => {
    if (nameSuggestion.data?.name) {
      setName(nameSuggestion.data.name)
    }
  }, [nameSuggestion.data])
  const nameProblem =
    step === 'name' && name.length > 0 ?
      indexNameProblem(name, existingIndexes?.map((index) => index.name) ?? [])
    : undefined
  const choices = [ALL_STRINGS, ...properties.map((candidate) => candidate.name)].filter(
    (candidate) => !existingIndexes?.some((index) => index.label === label && index.property === candidate)
  )

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (step === 'select') {
      if (label && property) {
        setName('')
        setStep('name')
      }
      return
    }
    if (!name || nameProblem) return
    try {
      await create({ projectId, label, property, name })
      onCreated()
    } catch {
      // surfaced by the mutation's error toast
    }
  }

  const picker = (text: string, placeholder: string, disabled = false) => (
    <Button
      className="w-full justify-between font-normal"
      disabled={disabled}
      size="small"
      type="button"
      variant="outline"
    >
      <span className={text ? '' : 'text-content3'}>{text || placeholder}</span>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  )

  return (
    <form className="mt-6 flex flex-col gap-5" onSubmit={submit}>
      {step === 'name' ?
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <LabelName idx={labelIdx} label={label} />
            <span className="text-content3">:</span>
            <span className="w-fit rounded-sm bg-content3/10 px-1 font-mono text-xs text-content2">
              {propertyText(property)}
            </span>
          </div>
          <TextField
            autoFocus
            caption="Unique within this project. API clients use it to search the index."
            disabled={nameSuggestion.isFetching}
            error={
              nameProblem ?? (nameSuggestion.isError ? 'Could not suggest a name; enter one.' : undefined)
            }
            label="Index name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder={nameSuggestion.isFetching ? 'Suggesting a name…' : 'Index name'}
            size="small"
            value={name}
          />
        </div>
      : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Label</span>
            {loadingLabels ?
              <Skeleton enabled>{picker('', 'Loading…', true)}</Skeleton>
            : <SearchSelect modal trigger={picker(label, 'Select a label')}>
                {labelEntries.map(([labelName], idx) => (
                  <SelectItem
                    key={labelName}
                    onSelect={() => {
                      setLabel(labelName)
                      setProperty('')
                    }}
                    value={labelName}
                  >
                    <LabelName idx={idx} label={labelName} />
                  </SelectItem>
                ))}
              </SearchSelect>
            }
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Property</span>
            {!label ?
              picker('', 'Select a label first', true)
            : loadingProperties ?
              <Skeleton enabled>{picker('', 'Loading…', true)}</Skeleton>
            : <SearchSelect modal trigger={picker(propertyText(property), 'Select a text property')}>
                {choices.length === 0 ?
                  <SelectItem disabled value="">
                    Every text property is indexed
                  </SelectItem>
                : choices.map((candidate) => (
                    <SelectItem key={candidate} onSelect={() => setProperty(candidate)} value={candidate}>
                      {propertyText(candidate)}
                    </SelectItem>
                  ))
                }
              </SearchSelect>
            }
          </div>
        </div>
      }
      <div className="flex justify-end gap-2 border-t pt-5">
        {step === 'name' && (
          <Button onClick={() => setStep('select')} type="button" variant="outline">
            Back
          </Button>
        )}
        {step === 'select' ?
          <Button disabled={!label || !property} type="submit" variant="primary">
            Next
          </Button>
        : <Button
            disabled={!name || !!nameProblem || nameSuggestion.isFetching}
            loading={isPending}
            type="submit"
            variant="primary"
          >
            Create
          </Button>
        }
      </div>
    </form>
  )
}
